import React from 'react';
import { EvidenceTag } from '../../types/types';

interface TagDisplayProps {
  tags: EvidenceTag[];
  variant?: 'compact' | 'full';
  showModule?: boolean;
  maxDisplay?: number;
  className?: string;
}

/**
 * Component to display evidence tags with module colors
 */
export const TagDisplay: React.FC<TagDisplayProps> = ({
  tags = [],
  variant = 'compact',
  showModule = false,
  maxDisplay = 3,
  className = ''
}) => {
  if (!tags || tags.length === 0) {
    return null;
  }

  // Group tags by module for better organization
  const tagsByModule = tags.reduce((acc, tag) => {
    if (!acc[tag.module_id]) {
      acc[tag.module_id] = {
        module_name: tag.module_name,
        module_color: tag.module_color,
        streams: []
      };
    }
    acc[tag.module_id].streams.push(tag);
    return acc;
  }, {} as Record<string, { module_name: string; module_color: string; streams: EvidenceTag[] }>);

  // For compact view, show limited tags
  const displayTags = variant === 'compact' && maxDisplay < tags.length
    ? tags.slice(0, maxDisplay)
    : tags;

  const remainingCount = tags.length - displayTags.length;

  if (variant === 'full') {
    // Full view - group by module
    return (
      <div className={`space-y-2 ${className}`}>
        {Object.entries(tagsByModule).map(([moduleId, moduleData]) => (
          <div key={moduleId} className="flex flex-col gap-1">
            {showModule && (
              <div 
                className="text-xs font-semibold px-2 py-0.5 rounded-md inline-flex items-center self-start"
                style={{ 
                  backgroundColor: `${moduleData.module_color}20`,
                  color: moduleData.module_color 
                }}
              >
                {moduleData.module_name}
              </div>
            )}
            <div className="flex flex-wrap gap-1">
              {moduleData.streams.map((tag) => (
                <span
                  key={tag.stream_id}
                  className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium transition-all hover:scale-105"
                  style={{
                    backgroundColor: `${tag.module_color}15`,
                    borderLeft: `3px solid ${tag.module_color}`,
                    color: '#374151'
                  }}
                  title={`${tag.module_name}: ${tag.stream_name}`}
                >
                  {tag.stream_name}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Compact view - inline tags
  return (
    <div className={`flex flex-wrap items-center gap-1 ${className}`}>
      {displayTags.map((tag) => (
        <span
          key={tag.stream_id}
          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
          style={{
            backgroundColor: `${tag.module_color}15`,
            borderLeft: `2px solid ${tag.module_color}`,
            color: '#374151'
          }}
          title={`${tag.module_name}: ${tag.stream_name}`}
        >
          {showModule && (
            <span className="opacity-60 mr-1">
              {tag.module_name.split(' ').map(word => word[0]).join('')}:
            </span>
          )}
          <span className="truncate max-w-[150px]">
            {tag.stream_name.length > 30 
              ? `${tag.stream_name.substring(0, 30)}...` 
              : tag.stream_name}
          </span>
        </span>
      ))}
      {remainingCount > 0 && (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
          +{remainingCount} more
        </span>
      )}
    </div>
  );
};

/**
 * Component to display a single tag as a badge
 */
export const TagBadge: React.FC<{ tag: EvidenceTag; onClick?: () => void; removable?: boolean; onRemove?: () => void }> = ({
  tag,
  onClick,
  removable = false,
  onRemove
}) => {
  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium transition-all ${
        onClick ? 'cursor-pointer hover:scale-105' : ''
      }`}
      style={{
        backgroundColor: `${tag.module_color}15`,
        borderLeft: `3px solid ${tag.module_color}`,
        color: '#374151'
      }}
      onClick={onClick}
      title={`${tag.module_name}: ${tag.stream_name}`}
    >
      <span>{tag.stream_name}</span>
      {removable && (
        <button
          className="ml-1 text-gray-500 hover:text-red-600"
          onClick={(e) => {
            e.stopPropagation();
            onRemove?.();
          }}
        >
          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      )}
    </span>
  );
};